// Point types
export type AnchorPoint = {
  x: number; y: number;
  leftDirection?: { x: number; y: number };
  rightDirection?: { x: number; y: number };
  type: "corner" | "smooth" | "symmetric";
};

export interface SubPath {
  points: AnchorPoint[];
  closed: boolean;
}

export interface PathModel {
  subpaths: SubPath[];
  transform?: { tx: number; ty: number; scaleX: number; scaleY: number };
}

// Create a simple straight line subpath
export function createLinePath(x1: number, y1: number, x2: number, y2: number): PathModel {
  return {
    subpaths: [{
      points: [
        { x: x1, y: y1, type: "corner" },
        { x: x2, y: y2, type: "corner" },
      ],
      closed: false,
    }],
  };
}

// Create a rectangle path
export function createRectPath(x: number, y: number, w: number, h: number): PathModel {
  return {
    subpaths: [{
      points: [
        { x, y, type: "corner" },
        { x: x + w, y, type: "corner" },
        { x: x + w, y: y + h, type: "corner" },
        { x, y: y + h, type: "corner" },
      ],
      closed: true,
    }],
  };
}

// Create an ellipse path (approximate with bezier curves)
export function createEllipsePath(cx: number, cy: number, rx: number, ry: number): PathModel {
  const k = 0.55228;
  const kx = rx * k;
  const ky = ry * k;
  return {
    subpaths: [{
      points: [
        { x: cx + rx, y: cy, type: "smooth", rightDirection: { x: cx + rx, y: cy - ky }, leftDirection: { x: cx + rx, y: cy + ky } },
        { x: cx, y: cy - ry, type: "smooth", rightDirection: { x: cx + kx, y: cy - ry }, leftDirection: { x: cx - kx, y: cy - ry } },
        { x: cx - rx, y: cy, type: "smooth", rightDirection: { x: cx - rx, y: cy - ky }, leftDirection: { x: cx - rx, y: cy + ky } },
        { x: cx, y: cy + ry, type: "smooth", rightDirection: { x: cx - kx, y: cy + ry }, leftDirection: { x: cx + kx, y: cy + ry } },
      ],
      closed: true,
    }],
  };
}

/**
 * Create a rounded rectangle path using bezier curves for the corners.
 * The radius is clamped to half the smaller dimension.
 */
export function createRoundedRectPath(x: number, y: number, w: number, h: number, radius: number): PathModel {
  const r = Math.min(radius, Math.min(w, h) / 2);
  if (r <= 0) return createRectPath(x, y, w, h);

  const k = 0.55228; // bezier approximation factor for quarter circles
  const kr = r * k;

  // Corner centers
  const tl = { cx: x + r, cy: y + r };
  const tr = { cx: x + w - r, cy: y + r };
  const br = { cx: x + w - r, cy: y + h - r };
  const bl = { cx: x + r, cy: y + h - r };

  return {
    subpaths: [{
      points: [
        // Top-left corner
        { x: x, y: tl.cy, type: "smooth", rightDirection: { x: x, y: tl.cy - kr }, leftDirection: { x: x, y: tl.cy + kr } },
        // Top-right corner
        { x: tr.cx, y: y, type: "smooth", rightDirection: { x: tr.cx + kr, y: y }, leftDirection: { x: tr.cx - kr, y: y } },
        // Bottom-right corner
        { x: x + w, y: br.cy, type: "smooth", rightDirection: { x: x + w, y: br.cy + kr }, leftDirection: { x: x + w, y: br.cy - kr } },
        // Bottom-left corner
        { x: bl.cx, y: y + h, type: "smooth", rightDirection: { x: bl.cx - kr, y: y + h }, leftDirection: { x: bl.cx + kr, y: y + h } },
      ],
      closed: true,
    }],
  };
}

/**
 * Create a regular polygon path centered at (cx, cy) with the given radius and number of sides.
 */
export function createPolygonPath(cx: number, cy: number, radius: number, sides: number): PathModel {
  const points: AnchorPoint[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2 - Math.PI / 2; // start from top
    points.push({
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
      type: "corner",
    });
  }
  return {
    subpaths: [{ points, closed: true }],
  };
}

/**
 * Create a star path centered at (cx, cy) with the given outer radius, inner radius, and points.
 */
export function createStarPath(cx: number, cy: number, outerRadius: number, innerRadius: number, points: number): PathModel {
  const anchors: AnchorPoint[] = [];
  for (let i = 0; i < points * 2; i++) {
    const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? outerRadius : innerRadius;
    anchors.push({
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
      type: "corner",
    });
  }
  return {
    subpaths: [{ points: anchors, closed: true }],
  };
}

/**
 * Create a line path with an arrowhead at the end.
 */
export function createLineWithArrowPath(x1: number, y1: number, x2: number, y2: number, arrowSize: number = 15): PathModel {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return createLinePath(x1, y1, x2, y2);

  const ux = dx / len;
  const uy = dy / len;
  // Perpendicular
  const px = -uy;
  const py = ux;

  // Arrowhead tip at end point, base inset from end
  const tipX = x2;
  const tipY = y2;
  const baseX = x2 - ux * arrowSize;
  const baseY = y2 - uy * arrowSize;
  const halfWidth = arrowSize * 0.4;

  return {
    subpaths: [
      {
        points: [
          { x: x1, y: y1, type: "corner" },
          { x: baseX, y: baseY, type: "corner" },
        ],
        closed: false,
      },
      {
        points: [
          { x: tipX, y: tipY, type: "corner" },
          { x: baseX + px * halfWidth, y: baseY + py * halfWidth, type: "corner" },
          { x: baseX - px * halfWidth, y: baseY - py * halfWidth, type: "corner" },
        ],
        closed: true,
      },
    ],
  };
}

/**
 * Create a custom shape path from a series of SVG-like path commands.
 * Supports: M (move), L (line), C (cubic bezier), Q (quadratic bezier), Z (close).
 */
export function createCustomShapePath(commands: Array<{ cmd: string; args: number[] }>): PathModel {
  const subpaths: SubPath[] = [];
  let currentPoints: AnchorPoint[] = [];
  let lastX = 0, lastY = 0;
  let controlX = 0, controlY = 0;

  for (const { cmd, args } of commands) {
    switch (cmd.toUpperCase()) {
      case "M":
        if (currentPoints.length > 0) {
          subpaths.push({ points: currentPoints, closed: false });
          currentPoints = [];
        }
        lastX = args[0] ?? 0;
        lastY = args[1] ?? 0;
        currentPoints.push({ x: lastX, y: lastY, type: "corner" });
        break;
      case "L":
        lastX = args[0] ?? lastX;
        lastY = args[1] ?? lastY;
        currentPoints.push({ x: lastX, y: lastY, type: "corner" });
        break;
      case "C": {
        // Cubic bezier: cp1x, cp1y, cp2x, cp2y, x, y
        const cp1x = args[0] ?? lastX;
        const cp1y = args[1] ?? lastY;
        const cp2x = args[2] ?? lastX;
        const cp2y = args[3] ?? lastY;
        const x = args[4] ?? lastX;
        const y = args[5] ?? lastY;
        currentPoints.push({
          x, y,
          type: "smooth",
          leftDirection: { x: cp1x, y: cp1y },
          rightDirection: { x: cp2x, y: cp2y },
        });
        lastX = x;
        lastY = y;
        controlX = cp2x;
        controlY = cp2y;
        break;
      }
      case "Q": {
        // Quadratic bezier: cpx, cpy, x, y
        const cpx = args[0] ?? lastX;
        const cpy = args[1] ?? lastY;
        const x = args[2] ?? lastX;
        const y = args[3] ?? lastY;
        currentPoints.push({
          x, y,
          type: "smooth",
          leftDirection: { x: cpx, y: cpy },
          rightDirection: { x: cpx, y: cpy },
        });
        lastX = x;
        lastY = y;
        break;
      }
      case "Z":
        if (currentPoints.length > 0) {
          subpaths.push({ points: currentPoints, closed: true });
          currentPoints = [];
        }
        break;
    }
  }
  if (currentPoints.length > 0) {
    subpaths.push({ points: currentPoints, closed: false });
  }
  return { subpaths };
}

/**
 * Boolean path operations: unite, subtract, intersect, exclude.
 * Functional approximation using bounding box and point-in-path tests.
 */
export type PathBooleanOp = "unite" | "subtract" | "intersect" | "exclude";

export function pathBooleanOp(a: PathModel, b: PathModel, op: PathBooleanOp): PathModel {
  // For MVP: return a combined path model. Full boolean ops require
  // a proper polygon clipping library (e.g., clipper-lib). This
  // approximation stores both subpaths and lets the renderer handle it.
  const combined: SubPath[] = [...a.subpaths.map(s => ({ ...s, points: [...s.points] }))];

  switch (op) {
    case "unite":
      // Simply combine subpaths
      combined.push(...b.subpaths.map(s => ({ ...s, points: [...s.points] })));
      break;
    case "subtract":
      // Store b subpaths with a flag for the renderer to subtract
      combined.push(...b.subpaths.map(s => ({ ...s, points: [...s.points], closed: true })));
      break;
    case "intersect":
      // Keep only overlapping regions — approximate by keeping both
      combined.push(...b.subpaths.map(s => ({ ...s, points: [...s.points], closed: true })));
      break;
    case "exclude":
      // XOR — keep both, renderer handles exclusion
      combined.push(...b.subpaths.map(s => ({ ...s, points: [...s.points], closed: true })));
      break;
  }

  return { subpaths: combined };
}

/**
 * Sample points along a path for text-on-path rendering.
 * Returns an array of {x, y, angle} positions along the path.
 */
export function samplePathForText(path: PathModel, numSamples: number): Array<{ x: number; y: number; angle: number }> {
  const samples: Array<{ x: number; y: number; angle: number }> = [];
  if (path.subpaths.length === 0) return samples;

  const subpath = path.subpaths[0]!;
  const pts = subpath.points;
  if (pts.length < 2) return samples;

  // Calculate total path length
  let totalLen = 0;
  const segments: Array<{ from: AnchorPoint; to: AnchorPoint; len: number }> = [];
  for (let i = 0; i < pts.length; i++) {
    const from = pts[i]!;
    const to = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    totalLen += len;
    segments.push({ from, to, len });
  }

  if (totalLen === 0) return samples;

  // Sample evenly along the path
  for (let i = 0; i < numSamples; i++) {
    const targetDist = (i / numSamples) * totalLen;
    let accumulated = 0;
    for (const seg of segments) {
      if (accumulated + seg.len >= targetDist) {
        const t = seg.len > 0 ? (targetDist - accumulated) / seg.len : 0;
        const x = seg.from.x + (seg.to.x - seg.from.x) * t;
        const y = seg.from.y + (seg.to.y - seg.from.y) * t;
        const angle = Math.atan2(seg.to.y - seg.from.y, seg.to.x - seg.from.x);
        samples.push({ x, y, angle });
        break;
      }
      accumulated += seg.len;
    }
  }

  return samples;
}
